import { useHistory, useQueryParams } from '@fs/zion-router'
import { useSlsDispatchContext, useSlsStateContext } from '@fs/zion-sls'
import { useCallback, useEffect, useRef } from 'react'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { getImagesFromApidList } from '../../../../shared/utils/getImagesFromApidList'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'

export default function useImageBrowserSetup() {
  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const history = useHistory()
  const { query } = useQueryParams()

  const zionSlsState = useSlsStateContext()
  const { currentImageArk } = zionSlsState
  const zionSlsDispatch = useSlsDispatchContext()

  const thumbnails = sharedBrowserState.thumbnails
  const { unifiedApidList } = sharedBrowserState
  const r2State = useR2StateContext()
  const { childGroups } = r2State

  const needsInit = useRef(true)
  const thumbnailsAdjusted = useRef(false)

  const regenerateThumbnailsIfNeeded = useCallback(
    (expectedViewMode) => {
      if (!childGroups?.length || !unifiedApidList?.length || thumbnailsAdjusted.current) {
        return false
      }

      const shouldBeAllImages = expectedViewMode === 'group'
      const thumbnailsAreAllImages = thumbnails?.length === unifiedApidList.length

      if (shouldBeAllImages && !thumbnailsAreAllImages) {
        const allThumbnails = getImagesFromApidList(unifiedApidList)
        sharedBrowserDispatch({ type: 'SET_THUMBNAILS', value: allThumbnails })
        thumbnailsAdjusted.current = true
        return true // Signal that we're waiting for regeneration
      }

      return false
    },
    [childGroups, unifiedApidList, thumbnails, sharedBrowserDispatch]
  )

  const findChildGroupForIndex = useCallback(
    (globalIdx) => {
      let cumulativeCount = 0

      const result = childGroups.reduce(
        (acc, child) => {
          if (acc.childGroup) return acc // Already found

          const childImageCount = child.childIds?.length || 0
          if (globalIdx < cumulativeCount + childImageCount) {
            return {
              childGroup: child,
              localIndex: globalIdx - cumulativeCount,
            }
          }
          cumulativeCount += childImageCount
          return acc
        },
        { childGroup: null, localIndex: -1 }
      )

      return result
    },
    [childGroups]
  )

  const updateGroupIdInUrl = useCallback(
    (correctGroupId) => {
      const searchParams = new URLSearchParams(window.location.search)
      if (correctGroupId) {
        searchParams.set('groupId', correctGroupId)
      } else {
        searchParams.delete('groupId')
      }
      history.replace({
        pathname: window.location.pathname,
        search: `?${searchParams.toString()}`,
      })
    },
    [history]
  )

  const findIndexWithChildGroups = useCallback(
    (imageArk, expectedViewMode) => {
      const parentThumbnails = getImagesFromApidList(unifiedApidList)
      const globalIdx = parentThumbnails?.findIndex((img) => img?.ark === imageArk) ?? -1

      if (globalIdx === -1) return -1

      const { childGroup: correctChildGroup, localIndex: indexInCorrectGroup } = findChildGroupForIndex(globalIdx)

      if (correctChildGroup && correctChildGroup.parentId !== query?.groupId) {
        updateGroupIdInUrl(correctChildGroup.parentId)
        return -1 // Signal to wait for thumbnails to update
      }

      return expectedViewMode === 'group' ? globalIdx : indexInCorrectGroup
    },
    [unifiedApidList, query?.groupId, findChildGroupForIndex, updateGroupIdInUrl]
  )

  const findImageIndex = useCallback(
    (imageArk, expectedViewMode) => {
      const hasChildGroups = childGroups?.length > 0 && unifiedApidList?.length > 0

      if (hasChildGroups) {
        return findIndexWithChildGroups(imageArk, expectedViewMode)
      }

      return thumbnails.findIndex((img) => img.ark === imageArk)
    },
    [childGroups, unifiedApidList, thumbnails, findIndexWithChildGroups]
  )

  const setIndexFromUrlArk = useCallback(() => {
    const imageArk = history.location.pathname.split('/').pop()
    if (!currentImageArk) {
      zionSlsDispatch({ type: 'SET_DATA', value: { currentImageArk: imageArk } })
    }

    const expectedViewMode = query?.cat ? 'group' : 'single'

    if (regenerateThumbnailsIfNeeded(expectedViewMode)) {
      return // Wait for next render with updated thumbnails
    }

    if (!thumbnails?.length) return

    const idx = findImageIndex(imageArk, expectedViewMode)

    if (needsInit.current && idx > -1 && idx < thumbnails.length) {
      needsInit.current = false
      sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value: idx })
    }
  }, [
    currentImageArk,
    history,
    query?.cat,
    sharedBrowserDispatch,
    thumbnails,
    zionSlsDispatch,
    regenerateThumbnailsIfNeeded,
    findImageIndex,
  ])

  // Initialize the focused index
  // Don’t load the image viewer into memory until the focusedIndex and images have been set.  Otherwise it will load image zero and then load the correct image
  // The first time focusedIndex is set, it's set from the ark in the browser url. We do this by finding it's index in the thumbnails, which were set from the apids
  useEffect(() => {
    setIndexFromUrlArk()
  }, [setIndexFromUrlArk])
}
